import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { serviceFeeApi, CasualParkingVoucherReport } from '../api/serviceFeeApi';
import { generateBillingPeriods } from '../utils/billingPeriods';
import { formatVndAmount } from '../utils/vndFormat';
import { getApiErrorMessage } from '../utils/apiErrors';
import { formatDateToDisplay } from '../utils/apartmentDate';
import { downloadExcel, sanitizeExportFilename } from '../utils/excelExport';
import { BillDetailsModal } from '../components/modals/BillDetailsModal';

interface CasualParkingVoucherReportTabProps {
  buildingId: string;
}

type VoucherDetailRow = CasualParkingVoucherReport['rows'][number]['details'][number];

const compareVoucherDates = (a: VoucherDetailRow, b: VoucherDetailRow): number => {
  const ta = a.collectedOn ? new Date(a.collectedOn).getTime() : 0;
  const tb = b.collectedOn ? new Date(b.collectedOn).getTime() : 0;
  if (ta !== tb) return ta - tb;
  return a.id.localeCompare(b.id);
};

const exportCounterpartyVoucherExcel = (
  counterpartyName: string,
  billingPeriod: string,
  details: VoucherDetailRow[],
) => {
  const sorted = [...details].sort(compareVoucherDates);
  const headers = ['Ngày thu', 'Số tiền voucher', 'Ghi chú'];
  const dataRows = sorted.map((d) => [
    formatDateToDisplay(d.collectedOn),
    d.amount,
    d.notes?.trim() || '',
  ]);
  const total = sorted.reduce((sum, d) => sum + d.amount, 0);
  dataRows.push(['Tổng cộng', total, '']);

  const filename = `voucher-xe-vang-lai-${sanitizeExportFilename(counterpartyName)}-ky-${billingPeriod.replace('/', '-')}.xlsx`;
  downloadExcel('Voucher', headers, dataRows, filename);
  toast.success('Đã xuất file Excel');
};

export const CasualParkingVoucherReportTab: React.FC<CasualParkingVoucherReportTabProps> = ({
  buildingId,
}) => {
  const periods = generateBillingPeriods();
  const [billingPeriod, setBillingPeriod] = useState(periods[0]?.value ?? '');
  const [report, setReport] = useState<CasualParkingVoucherReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [unsettledOnly, setUnsettledOnly] = useState(false);
  const [settlingId, setSettlingId] = useState<string | null>(null);
  const [counterpartySearch, setCounterpartySearch] = useState('');
  const [receiptBillId, setReceiptBillId] = useState<string | null>(null);
  const [bulkSettlingCounterparty, setBulkSettlingCounterparty] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!buildingId || !billingPeriod) return;
    setLoading(true);
    try {
      const data = await serviceFeeApi.getCasualParkingVoucherReport(buildingId, billingPeriod, {
        unsettledOnly,
      });
      setReport(data);
    } catch {
      toast.error('Không tải được báo cáo voucher');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [buildingId, billingPeriod, unsettledOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRows = useMemo(() => {
    if (!report?.rows.length) return [];
    const q = counterpartySearch.trim().toLowerCase();
    if (!q) return report.rows;
    return report.rows.filter((row) => row.counterpartyName.toLowerCase().includes(q));
  }, [report?.rows, counterpartySearch]);

  const settleCounterpartyForPeriod = async (counterpartyName: string) => {
    const unsettledCount = report?.rows
      .find((r) => r.counterpartyName === counterpartyName)
      ?.details.filter((d) => !d.settledAt).length;
    if (!unsettledCount) {
      toast.error('Không còn dòng chưa đối soát');
      return;
    }
    const confirmed = window.confirm(
      `Đánh dấu đã thu từ đối tác cho ${counterpartyName} — kỳ ${billingPeriod} (${unsettledCount} dòng)?`,
    );
    if (!confirmed) return;

    const input = window.prompt(
      `Ghi chú đối soát voucher — ${counterpartyName} (tùy chọn):`,
      '',
    );
    if (input === null) return;

    setBulkSettlingCounterparty(counterpartyName);
    try {
      const result = await serviceFeeApi.bulkCasualParkingVoucherSettlement({
        buildingId,
        billingPeriod,
        counterpartyName,
        settled: true,
        settledNotes: input.trim() || undefined,
      });
      toast.success(`Đã đối soát ${result.updatedCount} dòng voucher`);
      await load();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Không cập nhật được trạng thái voucher'));
    } finally {
      setBulkSettlingCounterparty(null);
    }
  };

  const toggleSettlement = async (
    lineId: string,
    settled: boolean,
    counterpartyName: string,
  ) => {
    let settledNotes: string | undefined;
    if (settled) {
      const input = window.prompt(
        `Ghi chú đối soát voucher — ${counterpartyName} (tùy chọn):`,
        '',
      );
      if (input === null) return;
      settledNotes = input.trim() || undefined;
    }
    setSettlingId(lineId);
    try {
      await serviceFeeApi.updateCasualParkingVarianceSettlement(lineId, {
        settled,
        settledNotes,
      });
      toast.success(settled ? 'Đã đánh dấu đã thu từ đối tác' : 'Đã bỏ đánh dấu đối soát');
      await load();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Không cập nhật được trạng thái voucher'));
    } finally {
      setSettlingId(null);
    }
  };

  if (!buildingId) {
    return <p className="text-sm text-gray-500 py-8 text-center">Chọn chung cư để xem báo cáo.</p>;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Tổng hợp voucher xe vãng lai theo đối tác trong kỳ. Kế toán đánh dấu khi đã thu hộ từ ngân
        hàng / công ty. Xuất Excel theo đối tượng để gửi khách (tạo HĐ thu trả sau thủ công).
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Kỳ thu phí</label>
          <select
            className="border rounded px-3 py-2 text-sm min-w-[160px]"
            value={billingPeriod}
            onChange={(e) => setBillingPeriod(e.target.value)}
          >
            {periods.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Tìm đối tượng</label>
          <input
            type="search"
            className="border rounded px-3 py-2 text-sm min-w-[200px]"
            placeholder="Tên đối tác..."
            value={counterpartySearch}
            onChange={(e) => setCounterpartySearch(e.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
          <input
            type="checkbox"
            checked={unsettledOnly}
            onChange={(e) => setUnsettledOnly(e.target.checked)}
          />
          Chỉ còn phải thu
        </label>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="px-4 py-2 text-sm border rounded hover:bg-gray-50 disabled:opacity-50"
        >
          {loading ? 'Đang tải...' : 'Tải lại'}
        </button>
      </div>

      {report ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
          <div className="rounded-lg border bg-blue-50 border-blue-100 px-4 py-3">
            <div className="text-gray-600">Tổng voucher (phải thu đối tác)</div>
            <div className="text-xl font-semibold text-blue-800">
              {formatVndAmount(report.voucherTotal)}
            </div>
          </div>
          <div className="rounded-lg border bg-emerald-50 border-emerald-100 px-4 py-3">
            <div className="text-gray-600">Đã đối soát / thu hộ</div>
            <div className="text-xl font-semibold text-emerald-800">
              {formatVndAmount(report.settledVoucherTotal)}
            </div>
          </div>
          <div className="rounded-lg border bg-amber-50 border-amber-100 px-4 py-3">
            <div className="text-gray-600">Còn phải thu</div>
            <div className="text-xl font-semibold text-amber-900">
              {formatVndAmount(report.outstandingVoucherTotal)}
            </div>
          </div>
          <div className="rounded-lg border bg-gray-50 px-4 py-3">
            <div className="text-gray-600">Tổng khấu trừ phí (kỳ {report.billingPeriod})</div>
            <div className="text-xl font-semibold">{formatVndAmount(report.deductionTotal)}</div>
            {report.deductionDetails?.length ? (
              <p className="text-xs text-gray-500 mt-1">
                {report.deductionDetails.length} dòng trên {report.deductionDetails.length} hóa đơn (cả tòa)
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      {report?.deductionDetails?.length ? (
        <div className="rounded-lg border bg-gray-50/80 px-4 py-3 text-sm">
          <div className="font-medium text-gray-800 mb-2">Chi tiết khấu trừ phí (theo hóa đơn)</div>
          <ul className="space-y-1 text-gray-700 text-xs">
            {report.deductionDetails.map((d) => (
              <li key={d.id}>
                <strong>{d.apartmentCode}</strong> · {d.counterpartyName}:{' '}
                <strong>{formatVndAmount(d.amount)}</strong>
                {d.machineAmount != null ? (
                  <span className="text-gray-500">
                    {' '}
                    (máy {formatVndAmount(d.machineAmount)}, thực thu {formatVndAmount(d.actualAmount)})
                  </span>
                ) : null}
                {d.notes ? ` — ${d.notes}` : ''}
              </li>
            ))}
          </ul>
          <p className="text-xs text-gray-500 mt-2">
            Tổng khấu trừ gộp tất cả hóa đơn xe vãng lai trong kỳ, không chỉ một phiếu thu.
          </p>
        </div>
      ) : null}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-b-2 border-cyan-500 rounded-full" />
        </div>
      ) : !report?.rows.length ? (
        <p className="text-center text-gray-500 py-12 text-sm">
          {unsettledOnly
            ? 'Không còn voucher chưa đối soát trong kỳ này.'
            : 'Chưa có voucher xe vãng lai trong kỳ này.'}
        </p>
      ) : !filteredRows.length ? (
        <p className="text-center text-gray-500 py-12 text-sm">
          Không có đối tượng khớp &quot;{counterpartySearch.trim()}&quot;.
        </p>
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left">Đối tượng</th>
                <th className="px-3 py-2 text-right">Số dòng</th>
                <th className="px-3 py-2 text-right">Tổng voucher</th>
                <th className="px-3 py-2 text-right">Đã thu</th>
                <th className="px-3 py-2 text-right">Còn phải thu</th>
                <th className="px-3 py-2 text-left">Thao tác</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredRows.map((row) => {
                const sortedDetails = [...row.details].sort(compareVoucherDates);
                return (
                  <React.Fragment key={row.counterpartyName}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium">{row.counterpartyName}</td>
                      <td className="px-3 py-2 text-right">{row.lineCount}</td>
                      <td className="px-3 py-2 text-right font-medium">
                        {formatVndAmount(row.totalAmount)}
                      </td>
                      <td className="px-3 py-2 text-right text-emerald-700">
                        {formatVndAmount(row.settledAmount)}
                      </td>
                      <td className="px-3 py-2 text-right text-amber-800">
                        {formatVndAmount(row.outstandingAmount)}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            className="text-cyan-700 hover:underline"
                            onClick={() =>
                              setExpanded((prev) =>
                                prev === row.counterpartyName ? null : row.counterpartyName,
                              )
                            }
                          >
                            {expanded === row.counterpartyName ? 'Thu gọn' : 'Xem'}
                          </button>
                          <button
                            type="button"
                            className="text-emerald-700 hover:underline"
                            onClick={() =>
                              exportCounterpartyVoucherExcel(
                                row.counterpartyName,
                                report!.billingPeriod,
                                row.details,
                              )
                            }
                          >
                            Xuất Excel
                          </button>
                          {row.outstandingAmount > 0 ? (
                            <button
                              type="button"
                              disabled={bulkSettlingCounterparty === row.counterpartyName}
                              className="text-emerald-800 hover:underline disabled:opacity-50"
                              onClick={() => settleCounterpartyForPeriod(row.counterpartyName)}
                            >
                              {bulkSettlingCounterparty === row.counterpartyName
                                ? 'Đang lưu...'
                                : 'Đã thu từ đối tác'}
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                    {expanded === row.counterpartyName ? (
                      <tr>
                        <td colSpan={6} className="px-3 py-2 bg-gray-50">
                          <ul className="space-y-2 text-xs text-gray-700">
                            {sortedDetails.map((d) => {
                              const isSettled = Boolean(d.settledAt);
                              const dateLabel = formatDateToDisplay(d.collectedOn) || '—';
                              return (
                                <li
                                  key={d.id}
                                  className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200/80 pb-2 last:border-0"
                                >
                                  <span>
                                    <strong>{dateLabel}</strong>
                                    {d.receiptBillId && d.receiptNumber ? (
                                      <>
                                        {' · '}
                                        <button
                                          type="button"
                                          className="text-cyan-700 hover:underline font-medium"
                                          onClick={() => setReceiptBillId(d.receiptBillId!)}
                                        >
                                          {d.receiptNumber}
                                        </button>
                                      </>
                                    ) : null}
                                    {' · '}
                                    {d.apartmentCode} · máy {formatVndAmount(d.machineAmount ?? 0)} ·
                                    thực thu {formatVndAmount(d.actualAmount)} ·{' '}
                                    <strong>{formatVndAmount(d.amount)}</strong>
                                    {d.notes ? ` — ${d.notes}` : ''}
                                    {isSettled ? (
                                      <span className="text-emerald-700 ml-1">
                                        (Đã đối soát
                                        {d.settledByName ? ` — ${d.settledByName}` : ''}
                                        {d.settledNotes ? `: ${d.settledNotes}` : ''})
                                      </span>
                                    ) : null}
                                  </span>
                                  <button
                                    type="button"
                                    disabled={settlingId === d.id}
                                    className={`shrink-0 px-2 py-1 rounded border text-xs ${
                                      isSettled
                                        ? 'border-gray-300 text-gray-600 hover:bg-white'
                                        : 'border-emerald-600 text-emerald-700 hover:bg-emerald-50'
                                    } disabled:opacity-50`}
                                    onClick={() =>
                                      toggleSettlement(d.id, !isSettled, row.counterpartyName)
                                    }
                                  >
                                    {settlingId === d.id
                                      ? '...'
                                      : isSettled
                                        ? 'Bỏ đánh dấu'
                                        : 'Đã thu từ đối tác'}
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <BillDetailsModal
        isOpen={Boolean(receiptBillId)}
        onClose={() => setReceiptBillId(null)}
        billId={receiptBillId ?? ''}
      />
    </div>
  );
};

export default CasualParkingVoucherReportTab;
